import React, { useRef, useCallback } from 'react';

export const RangeSlider: React.FC<{
  label: string;
  valueMin?: number;
  valueMax?: number;
  minLimit: number;
  maxLimit: number;
  step: number;
  onChange: (min?: number, max?: number) => void;
  unit?: string;
}> = ({ label, valueMin, valueMax, minLimit, maxLimit, step, onChange, unit }) => {
  const activeColor = '#64748b';
  const trackRef = useRef<HTMLDivElement>(null);
  const minValue = valueMin ?? minLimit;
  const maxValue = valueMax ?? maxLimit;
  const clampedMin = Math.max(minLimit, Math.min(minValue, maxValue));
  const clampedMax = Math.min(maxLimit, Math.max(maxValue, clampedMin));
  const range = maxLimit - minLimit || 1;
  const minPct = ((clampedMin - minLimit) / range) * 100;
  const maxPct = ((clampedMax - minLimit) / range) * 100;

  const getValueFromX = useCallback(
    (clientX: number) => {
      if (!trackRef.current) return minLimit;
      const rect = trackRef.current.getBoundingClientRect();
      const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      return Math.round((minLimit + pct * range) / step) * step;
    },
    [minLimit, range, step]
  );

  const draggingRef = useRef<'min' | 'max' | null>(null);

  const handlePointerDown = (thumb: 'min' | 'max') => (e: React.PointerEvent) => {
    draggingRef.current = thumb;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!draggingRef.current) return;
    const val = getValueFromX(e.clientX);
    if (draggingRef.current === 'min') {
      onChange(Math.min(val, clampedMax), clampedMax);
    } else {
      onChange(clampedMin, Math.max(val, clampedMin));
    }
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    if (draggingRef.current) {
      (e.target as HTMLElement).releasePointerCapture(e.pointerId);
      draggingRef.current = null;
    }
  };

  const parseNumber = (value: string): number | undefined => {
    const num = Number(value);
    return Number.isFinite(num) ? num : undefined;
  };

  const handleMinInput = (val?: number) => {
    if (val === undefined) {
      onChange(undefined, clampedMax);
    } else {
      onChange(Math.min(Math.max(val, minLimit), clampedMax), clampedMax);
    }
  };

  const handleMaxInput = (val?: number) => {
    if (val === undefined) {
      onChange(clampedMin, undefined);
    } else {
      onChange(clampedMin, Math.max(Math.min(val, maxLimit), clampedMin));
    }
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">{label}</label>

      {/* Track + thumbs */}
      <div className="relative px-4 py-4">
        <div
          ref={trackRef}
          className="relative h-8 select-none touch-none"
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
        >
          {/* Background track */}
          <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-[3px] rounded-full bg-gray-300" />

          {/* Selected range */}
          <div
            className="absolute top-1/2 -translate-y-1/2 h-[3px] rounded-full"
            style={{
              left: `${minPct}%`,
              right: `${100 - maxPct}%`,
              backgroundColor: activeColor,
            }}
          />

          {/* Min thumb */}
          <div
            className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-white shadow-lg cursor-grab active:cursor-grabbing border border-gray-200 touch-none"
            style={{ left: `${minPct}%` }}
            onPointerDown={handlePointerDown('min')}
          />

          {/* Max thumb */}
          <div
            className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-white shadow-lg cursor-grab active:cursor-grabbing border border-gray-200 touch-none"
            style={{ left: `${maxPct}%` }}
            onPointerDown={handlePointerDown('max')}
          />
        </div>
      </div>

      {/* Numeric inputs */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-col items-center min-w-[96px]">
          <span className="block text-[8px] text-gray-500 mb-0.5 text-center">Minimum</span>
          <input
            type="text"
            inputMode="numeric"
            value={unit === 'cm' ? `${valueMin ?? ''}${valueMin !== undefined ? ' cm' : ''}` : `${valueMin ?? ''}`}
            onChange={(e) => handleMinInput(parseNumber(e.target.value.replace(/[^0-9.-]/g, '')))}
            placeholder={unit === 'cm' ? `${minLimit} cm` : `${minLimit}${unit ?? ''}`}
            className="w-[82px] px-2 py-1 border border-gray-300 rounded-full text-[10px] text-center focus:outline-none focus:ring-2 focus:ring-slate-400"
          />
        </div>
        <div className="flex flex-col items-center min-w-[96px]">
          <span className="block text-[8px] text-gray-500 mb-0.5 text-center">Maximum</span>
          <input
            type="text"
            inputMode="numeric"
            value={unit === 'cm' ? `${valueMax ?? ''}${valueMax !== undefined ? ' cm' : ''}` : `${valueMax ?? ''}`}
            onChange={(e) => handleMaxInput(parseNumber(e.target.value.replace(/[^0-9.-]/g, '')))}
            placeholder={unit === 'cm' ? `${maxLimit} cm+` : `${maxLimit}${unit ?? ''}+`}
            className="w-[82px] px-2 py-1 border border-gray-300 rounded-full text-[10px] text-center focus:outline-none focus:ring-2 focus:ring-slate-400"
          />
        </div>
      </div>
    </div>
  );
};
